import {Grid} from '@material-ui/core';
import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {useQuoteFormValidation} from 'Quoting/components/hooks/useQuoteFormValidation';
import {FormRowGrid, GridRowProps} from 'Quoting/components/shared/GridRow';
import {QuoteFormElement} from 'Quoting/components/shared/QuoteFormElement';
import {FormWrapper} from 'Quoting/components/shared/QuoteFormWrapper';
import {A360ProductCd} from 'Quoting/models/Quoting';
import {isDefined} from 'Quoting/utils/utilities';
import React from 'react';
import {useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {getBenefitArrayPaths} from '../Common/PropertyPaths';
import {getFieldError, getValidationRuleset} from '../rules/rulesValidator';

const coverageIndex = 0;
const benefitName = 'coveredConditionsDetails';

const InitialDiagnosisBlock = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const handleValidationOnBlur = useQuoteFormValidation();

  const initialPath = getBenefitArrayPaths(selectedPlan?.planIndex, coverageIndex, benefitName, 0);

  const ConditionPercentageError = (attributeName: string, amount: number, path: string) =>
    getFieldError(
      `CICoveredConditions.${attributeName}`,
      getQuoteFieldValue<number>(path) * 100,
      getValidationRuleset(A360ProductCd.CI, {attributeName, amount})
    );

  const initialDiagnosis: GridRowProps[] = [
    {
      rowProps: {key: 'illness-conditions-row-1'},
      title: 'Critical Illness Conditions (Recommended Value)',
      items: [
        {
          key: 'illness-conditions-heart-attack',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Heart Attack (100%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.heartAttackPct)}
              id={initialPath.heartAttackPct}
              onChange={e => updateQuoteFieldValue(initialPath.heartAttackPct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(ConditionPercentageError('heartAttackPct', 200, initialPath.heartAttackPct))}
              helperText={ConditionPercentageError('heartAttackPct', 200, initialPath.heartAttackPct)}
              required
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'illness-conditions-stroke',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Stroke (100%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.strokePct)}
              id={initialPath.strokePct}
              onChange={e => updateQuoteFieldValue(initialPath.strokePct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(ConditionPercentageError('strokePct', 200, initialPath.strokePct))}
              helperText={ConditionPercentageError('strokePct', 200, initialPath.strokePct)}
              required
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'illness-conditions-renal-failure',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="End Stage Renal Failure (100%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.kidneyRenalFailurePct)}
              id={initialPath.kidneyRenalFailurePct}
              onChange={e => updateQuoteFieldValue(initialPath.kidneyRenalFailurePct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError('kidneyRenalFailurePct', 200, initialPath.kidneyRenalFailurePct)
              )}
              helperText={ConditionPercentageError('kidneyRenalFailurePct', 200, initialPath.kidneyRenalFailurePct)}
              required
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'illness-conditions-organ-failure',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Major Organ Failure (100%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.majorOrganFailurePct)}
              id={initialPath.majorOrganFailurePct}
              onChange={e => updateQuoteFieldValue(initialPath.majorOrganFailurePct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(ConditionPercentageError('majorOrganFailurePct', 200, initialPath.majorOrganFailurePct))}
              helperText={ConditionPercentageError('majorOrganFailurePct', 200, initialPath.majorOrganFailurePct)}
              required
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'illness-conditions-coronary-artery-disease',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Coronary Artery Disease–Coronary Artery Bypass Graft (25%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.coronaryArteryDiseasePct)}
              id={initialPath.coronaryArteryDiseasePct}
              onChange={e => updateQuoteFieldValue(initialPath.coronaryArteryDiseasePct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError('coronaryArteryDiseasePct', 200, initialPath.coronaryArteryDiseasePct)
              )}
              helperText={ConditionPercentageError(
                'coronaryArteryDiseasePct',
                200,
                initialPath.coronaryArteryDiseasePct
              )}
              required
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'illness-conditions-tia-rind',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="TIA or RIND (25%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.tiaOrRindPct)}
              id={initialPath.tiaOrRindPct}
              onChange={e => updateQuoteFieldValue(initialPath.tiaOrRindPct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(ConditionPercentageError('tiaOrRindPct', 200, initialPath.tiaOrRindPct))}
              helperText={ConditionPercentageError('tiaOrRindPct', 200, initialPath.tiaOrRindPct)}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'illness-conditions-crohn-disease',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Crohn’s Disease (25%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.crohnDiseasePct)}
              id={initialPath.crohnDiseasePct}
              onChange={e => updateQuoteFieldValue(initialPath.crohnDiseasePct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(ConditionPercentageError('crohnDiseasePct', 200, initialPath.crohnDiseasePct))}
              helperText={ConditionPercentageError('crohnDiseasePct', 200, initialPath.crohnDiseasePct)}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'illness-conditions-bonemarrow-stemcell-donation',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Bone Marrow or Stem Cell Donation (100%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.boneMarrowOrStemCellDonationPct)}
              id={initialPath.boneMarrowOrStemCellDonationPct}
              onChange={e =>
                updateQuoteFieldValue(initialPath.boneMarrowOrStemCellDonationPct, Number(e.target.value) / 100)
              }
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError(
                  'boneMarrowOrStemCellDonationPct',
                  200,
                  initialPath.boneMarrowOrStemCellDonationPct
                )
              )}
              helperText={ConditionPercentageError(
                'boneMarrowOrStemCellDonationPct',
                200,
                initialPath.boneMarrowOrStemCellDonationPct
              )}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'illness-conditions-bonemarrow-stemcell-transplant',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Bone Marrow or Stem Cell Transplant (100%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.boneMarrowOrStemCellTransplantPct)}
              id={initialPath.boneMarrowOrStemCellTransplantPct}
              onChange={e =>
                updateQuoteFieldValue(initialPath.boneMarrowOrStemCellTransplantPct, Number(e.target.value) / 100)
              }
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError(
                  'boneMarrowOrStemCellTransplantPct',
                  200,
                  initialPath.boneMarrowOrStemCellTransplantPct
                )
              )}
              helperText={ConditionPercentageError(
                'boneMarrowOrStemCellTransplantPct',
                200,
                initialPath.boneMarrowOrStemCellTransplantPct
              )}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
    {
      rowProps: {key: 'illness-conditions-row-2'},
      title: 'Cardio Conditions (Recommended Value)',
      items: [
        {
          key: 'cardio-conditions-cardiac-arrest',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Sudden Cardiac Arrest (25%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.suddenCardiacArrestPct)}
              id={initialPath.suddenCardiacArrestPct}
              onChange={e => updateQuoteFieldValue(initialPath.suddenCardiacArrestPct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError('suddenCardiacArrestPct', 200, initialPath.suddenCardiacArrestPct)
              )}
              helperText={ConditionPercentageError('suddenCardiacArrestPct', 200, initialPath.suddenCardiacArrestPct)}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'cardio-conditions-fibrosis',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Idiopathic Pulmonary Fibrosis (25%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.idiopathicPulmonaryFibrosisPct)}
              id={initialPath.idiopathicPulmonaryFibrosisPct}
              onChange={e =>
                updateQuoteFieldValue(initialPath.idiopathicPulmonaryFibrosisPct, Number(e.target.value) / 100)
              }
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError(
                  'idiopathicPulmonaryFibrosisPct',
                  200,
                  initialPath.idiopathicPulmonaryFibrosisPct
                )
              )}
              helperText={ConditionPercentageError(
                'idiopathicPulmonaryFibrosisPct',
                200,
                initialPath.idiopathicPulmonaryFibrosisPct
              )}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'cardio-conditions-pulmonary-embolism',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Pulmonary Embolism (25%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.pulmonaryEmbolismPct)}
              id={initialPath.pulmonaryEmbolismPct}
              onChange={e => updateQuoteFieldValue(initialPath.pulmonaryEmbolismPct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(ConditionPercentageError('pulmonaryEmbolismPct', 200, initialPath.pulmonaryEmbolismPct))}
              helperText={ConditionPercentageError('pulmonaryEmbolismPct', 200, initialPath.pulmonaryEmbolismPct)}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'cardio-conditions-angioplasty',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Coronary Artery Disease – Coronary Angioplasty (10%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.coronaryArteryDiseaseAngioplastyPct)}
              id={initialPath.coronaryArteryDiseaseAngioplastyPct}
              onChange={e =>
                updateQuoteFieldValue(initialPath.coronaryArteryDiseaseAngioplastyPct, Number(e.target.value) / 100)
              }
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError(
                  'coronaryArteryDiseaseAngioplastyPct',
                  200,
                  initialPath.coronaryArteryDiseaseAngioplastyPct
                )
              )}
              helperText={ConditionPercentageError(
                'coronaryArteryDiseaseAngioplastyPct',
                200,
                initialPath.coronaryArteryDiseaseAngioplastyPct
              )}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'cardio-conditions-aortic-valve',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Aortic or Mitral Valve Repair or Replacement (10%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.aorticOrMitralValveRepairOrReplacementPct)}
              id={initialPath.aorticOrMitralValveRepairOrReplacementPct}
              onChange={e =>
                updateQuoteFieldValue(
                  initialPath.aorticOrMitralValveRepairOrReplacementPct,
                  Number(e.target.value) / 100
                )
              }
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError(
                  'aorticOrMitralValveRepairOrReplacementPct',
                  200,
                  initialPath.aorticOrMitralValveRepairOrReplacementPct
                )
              )}
              helperText={ConditionPercentageError(
                'aorticOrMitralValveRepairOrReplacementPct',
                200,
                initialPath.aorticOrMitralValveRepairOrReplacementPct
              )}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'cardio-conditions-arrhythmia-pacemaker',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Arrhythmia - Pacemaker Placement (10%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.arrhythmiaPacemakerPlacementPct)}
              id={initialPath.arrhythmiaPacemakerPlacementPct}
              onChange={e =>
                updateQuoteFieldValue(initialPath.arrhythmiaPacemakerPlacementPct, Number(e.target.value) / 100)
              }
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError(
                  'arrhythmiaPacemakerPlacementPct',
                  200,
                  initialPath.arrhythmiaPacemakerPlacementPct
                )
              )}
              helperText={ConditionPercentageError(
                'arrhythmiaPacemakerPlacementPct',
                200,
                initialPath.arrhythmiaPacemakerPlacementPct
              )}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'cardio-conditions-arrhythmia-icd',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Arrhythmia - ICD Placement (10%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.arrhythmiaICDPlacementPct)}
              id={initialPath.arrhythmiaICDPlacementPct}
              onChange={e => updateQuoteFieldValue(initialPath.arrhythmiaICDPlacementPct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError('arrhythmiaICDPlacementPct', 200, initialPath.arrhythmiaICDPlacementPct)
              )}
              helperText={ConditionPercentageError(
                'arrhythmiaICDPlacementPct',
                200,
                initialPath.arrhythmiaICDPlacementPct
              )}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
    {
      rowProps: {key: 'illness-conditions-row-3'},
      title: 'Supplemental Conditions (Recommended Value)',
      items: [
        {
          key: 'supplemental-conditions-alzheimer',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Advanced Alzheimer’s (100%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.advancedAlzheimersPct)}
              id={initialPath.advancedAlzheimersPct}
              onChange={e => updateQuoteFieldValue(initialPath.advancedAlzheimersPct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError('advancedAlzheimersPct', 200, initialPath.advancedAlzheimersPct)
              )}
              helperText={ConditionPercentageError('advancedAlzheimersPct', 200, initialPath.advancedAlzheimersPct)}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'supplemental-conditions-parkinson',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Advanced Parkinson’s (100%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.advancedParkinsonsPct)}
              id={initialPath.advancedParkinsonsPct}
              onChange={e => updateQuoteFieldValue(initialPath.advancedParkinsonsPct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError('advancedParkinsonsPct', 200, initialPath.advancedParkinsonsPct)
              )}
              helperText={ConditionPercentageError('advancedParkinsonsPct', 200, initialPath.advancedParkinsonsPct)}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'supplemental-conditions-brain-tumor',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Benign Brain Tumor (100%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.benignBrainTumorPct)}
              id={initialPath.benignBrainTumorPct}
              onChange={e => updateQuoteFieldValue(initialPath.benignBrainTumorPct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(ConditionPercentageError('benignBrainTumorPct', 200, initialPath.benignBrainTumorPct))}
              helperText={ConditionPercentageError('benignBrainTumorPct', 200, initialPath.benignBrainTumorPct)}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'supplemental-conditions-coma',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Coma (100%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.comaPct)}
              id={initialPath.comaPct}
              onChange={e => updateQuoteFieldValue(initialPath.comaPct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(ConditionPercentageError('comaPct', 200, initialPath.comaPct))}
              helperText={ConditionPercentageError('comaPct', 200, initialPath.comaPct)}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'supplemental-conditions-loss-of-hearing',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Loss of Hearing (100%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.lossOfHearingPct)}
              id={initialPath.lossOfHearingPct}
              onChange={e => updateQuoteFieldValue(initialPath.lossOfHearingPct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(ConditionPercentageError('lossOfHearingPct', 200, initialPath.lossOfHearingPct))}
              helperText={ConditionPercentageError('lossOfHearingPct', 200, initialPath.lossOfHearingPct)}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'supplemental-conditions-loss-of-sight',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Loss of Sight (100%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.lossOfSightPct)}
              id={initialPath.lossOfSightPct}
              onChange={e => updateQuoteFieldValue(initialPath.lossOfSightPct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(ConditionPercentageError('lossOfSightPct', 200, initialPath.lossOfSightPct))}
              helperText={ConditionPercentageError('lossOfSightPct', 200, initialPath.lossOfSightPct)}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'supplemental-conditions-loss-of-speech',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Loss of Speech (100%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.lossOfSpeechPct)}
              id={initialPath.lossOfSpeechPct}
              onChange={e => updateQuoteFieldValue(initialPath.lossOfSpeechPct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(ConditionPercentageError('lossOfSpeechPct', 200, initialPath.lossOfSpeechPct))}
              helperText={ConditionPercentageError('lossOfSpeechPct', 200, initialPath.lossOfSpeechPct)}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'supplemental-conditions-paralysis',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Paralysis (100%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.paralysisPct)}
              id={initialPath.paralysisPct}
              onChange={e => updateQuoteFieldValue(initialPath.paralysisPct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(ConditionPercentageError('paralysisPct', 200, initialPath.paralysisPct))}
              helperText={ConditionPercentageError('paralysisPct', 200, initialPath.paralysisPct)}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'supplemental-conditions-hiv',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Occupational HIV (100%)"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(initialPath.occupationalHIVPct)}
              id={initialPath.occupationalHIVPct}
              onChange={e => updateQuoteFieldValue(initialPath.occupationalHIVPct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(ConditionPercentageError('occupationalHIVPct', 200, initialPath.occupationalHIVPct))}
              helperText={ConditionPercentageError('occupationalHIVPct', 200, initialPath.occupationalHIVPct)}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
  ];
  return <FormRowGrid rows={initialDiagnosis} />;
};

const RecurrenceDiagnosisBlock = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue} = useQuoteField();
  const handleValidationOnBlur = useQuoteFormValidation();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);

  const recurrencePath = getBenefitArrayPaths(selectedPlan?.planIndex, coverageIndex, benefitName, 1);

  const initialPath = getBenefitArrayPaths(selectedPlan?.planIndex, coverageIndex, benefitName, 0);

  const ConditionPercentageError = (attributeName: string, amount: number, path: string) =>
    getFieldError(
      `CICoveredConditions.${attributeName}`,
      getQuoteFieldValue<number>(path) * 100,
      getValidationRuleset(A360ProductCd.CI, {attributeName, amount})
    );

  const recurrenceDiagnosis: GridRowProps[] = [
    {
      rowProps: {key: 'recurrence-illness-conditions-row-1'},
      title: 'Critical Illness Conditions',
      items: [
        {
          key: 'recurrence-illness-conditions-heart-attack',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Heart Attack"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(recurrencePath.heartAttackPct)}
              id={recurrencePath.heartAttackPct}
              onChange={e => updateQuoteFieldValue(recurrencePath.heartAttackPct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError(
                  'heartAttackPct',
                  getQuoteFieldValue<number>(initialPath.heartAttackPct) * 100,
                  recurrencePath.heartAttackPct
                )
              )}
              helperText={ConditionPercentageError(
                'heartAttackPct',
                getQuoteFieldValue<number>(initialPath.heartAttackPct) * 100,
                recurrencePath.heartAttackPct
              )}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'recurrence-illness-conditions-stroke',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Stroke"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(recurrencePath.strokePct)}
              id={recurrencePath.strokePct}
              onChange={e => updateQuoteFieldValue(recurrencePath.strokePct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError(
                  'strokePct',
                  getQuoteFieldValue<number>(initialPath.strokePct) * 100,
                  recurrencePath.strokePct
                )
              )}
              helperText={ConditionPercentageError(
                'strokePct',
                getQuoteFieldValue<number>(initialPath.strokePct) * 100,
                recurrencePath.strokePct
              )}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'recurrence-illness-conditions-renal-failure',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="End Stage Renal Failure"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(recurrencePath.kidneyRenalFailurePct)}
              id={recurrencePath.kidneyRenalFailurePct}
              onChange={e => updateQuoteFieldValue(recurrencePath.kidneyRenalFailurePct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError(
                  'kidneyRenalFailurePct',
                  getQuoteFieldValue<number>(initialPath.kidneyRenalFailurePct) * 100,
                  recurrencePath.kidneyRenalFailurePct
                )
              )}
              helperText={ConditionPercentageError(
                'kidneyRenalFailurePct',
                getQuoteFieldValue<number>(initialPath.kidneyRenalFailurePct) * 100,
                recurrencePath.kidneyRenalFailurePct
              )}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'recurrence-illness-conditions-organ-failure',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Major Organ Failure"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(recurrencePath.majorOrganFailurePct)}
              id={recurrencePath.majorOrganFailurePct}
              onChange={e => updateQuoteFieldValue(recurrencePath.majorOrganFailurePct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError(
                  'majorOrganFailurePct',
                  getQuoteFieldValue<number>(initialPath.majorOrganFailurePct) * 100,
                  recurrencePath.majorOrganFailurePct
                )
              )}
              helperText={ConditionPercentageError(
                'majorOrganFailurePct',
                getQuoteFieldValue<number>(initialPath.majorOrganFailurePct) * 100,
                recurrencePath.majorOrganFailurePct
              )}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'recurrence-illness-conditions-coronary-artery-disease',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Coronary Artery Disease – Coronary Artery Bypass Graft"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(recurrencePath.coronaryArteryDiseasePct)}
              id={recurrencePath.coronaryArteryDiseasePct}
              onChange={e =>
                updateQuoteFieldValue(recurrencePath.coronaryArteryDiseasePct, Number(e.target.value) / 100)
              }
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError(
                  'coronaryArteryDiseasePct',
                  getQuoteFieldValue<number>(initialPath.coronaryArteryDiseasePct) * 100,
                  recurrencePath.coronaryArteryDiseasePct
                )
              )}
              helperText={ConditionPercentageError(
                'coronaryArteryDiseasePct',
                getQuoteFieldValue<number>(initialPath.coronaryArteryDiseasePct) * 100,
                recurrencePath.coronaryArteryDiseasePct
              )}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'recurrence-illness-conditions-tia-rind',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="TIA or RIND"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(recurrencePath.tiaOrRindPct)}
              id={recurrencePath.tiaOrRindPct}
              onChange={e => updateQuoteFieldValue(recurrencePath.tiaOrRindPct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError(
                  'tiaOrRindPct',
                  getQuoteFieldValue<number>(initialPath.tiaOrRindPct) * 100,
                  recurrencePath.tiaOrRindPct
                )
              )}
              helperText={ConditionPercentageError(
                'tiaOrRindPct',
                getQuoteFieldValue<number>(initialPath.tiaOrRindPct) * 100,
                recurrencePath.tiaOrRindPct
              )}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'recurrence-illness-conditions-crohn-disease',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Crohn’s Disease"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(recurrencePath.crohnDiseasePct)}
              id={recurrencePath.crohnDiseasePct}
              onChange={e => updateQuoteFieldValue(recurrencePath.crohnDiseasePct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError(
                  'crohnDiseasePct',
                  getQuoteFieldValue<number>(initialPath.crohnDiseasePct) * 100,
                  recurrencePath.crohnDiseasePct
                )
              )}
              helperText={ConditionPercentageError(
                'crohnDiseasePct',
                getQuoteFieldValue<number>(initialPath.crohnDiseasePct) * 100,
                recurrencePath.crohnDiseasePct
              )}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'recurrence-illness-conditions-bonemarrow-stemcell-donation',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Bone Marrow or Stem Cell Donation"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(recurrencePath.boneMarrowOrStemCellDonationPct)}
              id={recurrencePath.boneMarrowOrStemCellDonationPct}
              onChange={e =>
                updateQuoteFieldValue(recurrencePath.boneMarrowOrStemCellDonationPct, Number(e.target.value) / 100)
              }
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError(
                  'boneMarrowOrStemCellDonationPct',
                  getQuoteFieldValue<number>(initialPath.boneMarrowOrStemCellDonationPct) * 100,
                  recurrencePath.boneMarrowOrStemCellDonationPct
                )
              )}
              helperText={ConditionPercentageError(
                'boneMarrowOrStemCellDonationPct',
                getQuoteFieldValue<number>(initialPath.boneMarrowOrStemCellDonationPct) * 100,
                recurrencePath.boneMarrowOrStemCellDonationPct
              )}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'recurrence-illness-conditions-bonemarrow-stemcell-transplant',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Bone Marrow or Stem Cell Transplant"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(recurrencePath.boneMarrowOrStemCellTransplantPct)}
              id={recurrencePath.boneMarrowOrStemCellTransplantPct}
              onChange={e =>
                updateQuoteFieldValue(recurrencePath.boneMarrowOrStemCellTransplantPct, Number(e.target.value) / 100)
              }
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError(
                  'boneMarrowOrStemCellTransplantPct',
                  getQuoteFieldValue<number>(initialPath.boneMarrowOrStemCellTransplantPct) * 100,
                  recurrencePath.boneMarrowOrStemCellTransplantPct
                )
              )}
              helperText={ConditionPercentageError(
                'boneMarrowOrStemCellTransplantPct',
                getQuoteFieldValue<number>(initialPath.boneMarrowOrStemCellTransplantPct) * 100,
                recurrencePath.boneMarrowOrStemCellTransplantPct
              )}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
    {
      rowProps: {key: 'recurrence-illness-conditions-row-2'},
      title: 'Cardio Conditions',
      items: [
        {
          key: 'recurrence-cardio-conditions-cardiac-arrest',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Sudden Cardiac Arrest"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(recurrencePath.suddenCardiacArrestPct)}
              id={recurrencePath.suddenCardiacArrestPct}
              onChange={e => updateQuoteFieldValue(recurrencePath.suddenCardiacArrestPct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError(
                  'suddenCardiacArrestPct',
                  getQuoteFieldValue<number>(initialPath.suddenCardiacArrestPct) * 100,
                  recurrencePath.suddenCardiacArrestPct
                )
              )}
              helperText={ConditionPercentageError(
                'suddenCardiacArrestPct',
                getQuoteFieldValue<number>(initialPath.suddenCardiacArrestPct) * 100,
                recurrencePath.suddenCardiacArrestPct
              )}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'recurrence-cardio-conditions-fibrosis',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Idiopathic Pulmonary Fibrosis"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(recurrencePath.pulmonaryEmbolismPct)}
              id={recurrencePath.pulmonaryEmbolismPct}
              onChange={e => updateQuoteFieldValue(recurrencePath.pulmonaryEmbolismPct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError(
                  'pulmonaryEmbolismPct',
                  getQuoteFieldValue<number>(initialPath.pulmonaryEmbolismPct) * 100,
                  recurrencePath.pulmonaryEmbolismPct
                )
              )}
              helperText={ConditionPercentageError(
                'pulmonaryEmbolismPct',
                getQuoteFieldValue<number>(initialPath.pulmonaryEmbolismPct) * 100,
                recurrencePath.pulmonaryEmbolismPct
              )}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'recurrence-cardio-conditions-pulmonary-embolism',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Pulmonary Embolism"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(recurrencePath.pulmonaryEmbolismPct)}
              id={recurrencePath.pulmonaryEmbolismPct}
              onChange={e => updateQuoteFieldValue(recurrencePath.pulmonaryEmbolismPct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError(
                  'pulmonaryEmbolismPct',
                  getQuoteFieldValue<number>(initialPath.pulmonaryEmbolismPct) * 100,
                  recurrencePath.pulmonaryEmbolismPct
                )
              )}
              helperText={ConditionPercentageError(
                'pulmonaryEmbolismPct',
                getQuoteFieldValue<number>(initialPath.pulmonaryEmbolismPct) * 100,
                recurrencePath.pulmonaryEmbolismPct
              )}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'recurrence-cardio-conditions-angioplasty',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Coronary Artery Disease – Coronary Angioplasty"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(recurrencePath.coronaryArteryDiseaseAngioplastyPct)}
              id={recurrencePath.coronaryArteryDiseaseAngioplastyPct}
              onChange={e =>
                updateQuoteFieldValue(recurrencePath.coronaryArteryDiseaseAngioplastyPct, Number(e.target.value) / 100)
              }
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError(
                  'coronaryArteryDiseaseAngioplastyPct',
                  getQuoteFieldValue<number>(initialPath.coronaryArteryDiseaseAngioplastyPct) * 100,
                  recurrencePath.coronaryArteryDiseaseAngioplastyPct
                )
              )}
              helperText={ConditionPercentageError(
                'coronaryArteryDiseaseAngioplastyPct',
                getQuoteFieldValue<number>(initialPath.coronaryArteryDiseaseAngioplastyPct) * 100,
                recurrencePath.coronaryArteryDiseaseAngioplastyPct
              )}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'recurrence-cardio-conditions-aortic-valve',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Aortic or Mitral Valve Repair or Replacement"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(recurrencePath.aorticOrMitralValveRepairOrReplacementPct)}
              id={recurrencePath.aorticOrMitralValveRepairOrReplacementPct}
              onChange={e =>
                updateQuoteFieldValue(
                  recurrencePath.aorticOrMitralValveRepairOrReplacementPct,
                  Number(e.target.value) / 100
                )
              }
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError(
                  'aorticOrMitralValveRepairOrReplacementPct',
                  getQuoteFieldValue<number>(initialPath.aorticOrMitralValveRepairOrReplacementPct) * 100,
                  recurrencePath.aorticOrMitralValveRepairOrReplacementPct
                )
              )}
              helperText={ConditionPercentageError(
                'aorticOrMitralValveRepairOrReplacementPct',
                getQuoteFieldValue<number>(initialPath.aorticOrMitralValveRepairOrReplacementPct) * 100,
                recurrencePath.aorticOrMitralValveRepairOrReplacementPct
              )}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'recurrence-cardio-conditions-arrhythmia-pacemaker',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Arrhythmia - Pacemaker Placement"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(recurrencePath.arrhythmiaPacemakerPlacementPct)}
              id={recurrencePath.arrhythmiaPacemakerPlacementPct}
              onChange={e =>
                updateQuoteFieldValue(recurrencePath.arrhythmiaPacemakerPlacementPct, Number(e.target.value) / 100)
              }
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError(
                  'arrhythmiaPacemakerPlacementPct',
                  getQuoteFieldValue<number>(initialPath.arrhythmiaPacemakerPlacementPct) * 100,
                  recurrencePath.arrhythmiaPacemakerPlacementPct
                )
              )}
              helperText={ConditionPercentageError(
                'arrhythmiaPacemakerPlacementPct',
                getQuoteFieldValue<number>(initialPath.arrhythmiaPacemakerPlacementPct) * 100,
                recurrencePath.arrhythmiaPacemakerPlacementPct
              )}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'recurrence-cardio-conditions-arrhythmia-icd',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Arrhythmia - ICD Placement"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(recurrencePath.arrhythmiaICDPlacementPct)}
              id={recurrencePath.arrhythmiaICDPlacementPct}
              onChange={e =>
                updateQuoteFieldValue(recurrencePath.arrhythmiaICDPlacementPct, Number(e.target.value) / 100)
              }
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError(
                  'arrhythmiaICDPlacementPct',
                  getQuoteFieldValue<number>(initialPath.arrhythmiaICDPlacementPct) * 100,
                  recurrencePath.arrhythmiaICDPlacementPct
                )
              )}
              helperText={ConditionPercentageError(
                'arrhythmiaICDPlacementPct',
                getQuoteFieldValue<number>(initialPath.arrhythmiaICDPlacementPct) * 100,
                recurrencePath.arrhythmiaICDPlacementPct
              )}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
    {
      rowProps: {key: 'recurrence-illness-conditions-row-3'},
      title: 'Supplemental Conditions',
      items: [
        {
          key: 'recurrence-supplemental-conditions-alzheimer',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Advanced Alzheimer’s"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(recurrencePath.advancedAlzheimersPct)}
              id={recurrencePath.advancedAlzheimersPct}
              onChange={e => updateQuoteFieldValue(recurrencePath.advancedAlzheimersPct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError(
                  'advancedAlzheimersPct',
                  getQuoteFieldValue<number>(initialPath.advancedAlzheimersPct) * 100,
                  recurrencePath.advancedAlzheimersPct
                )
              )}
              helperText={ConditionPercentageError(
                'advancedAlzheimersPct',
                getQuoteFieldValue<number>(initialPath.advancedAlzheimersPct) * 100,
                recurrencePath.advancedAlzheimersPct
              )}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'recurrence-supplemental-conditions-parkinson',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Advanced Parkinson’s"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(recurrencePath.advancedParkinsonsPct)}
              id={recurrencePath.advancedParkinsonsPct}
              onChange={e => updateQuoteFieldValue(recurrencePath.advancedParkinsonsPct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError(
                  'advancedParkinsonsPct',
                  getQuoteFieldValue<number>(initialPath.advancedParkinsonsPct) * 100,
                  recurrencePath.advancedParkinsonsPct
                )
              )}
              helperText={ConditionPercentageError(
                'advancedParkinsonsPct',
                getQuoteFieldValue<number>(initialPath.advancedParkinsonsPct) * 100,
                recurrencePath.advancedParkinsonsPct
              )}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'recurrence-supplemental-conditions-brain-tumor',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Benign Brain Tumor"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(recurrencePath.benignBrainTumorPct)}
              id={recurrencePath.benignBrainTumorPct}
              onChange={e => updateQuoteFieldValue(recurrencePath.benignBrainTumorPct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError(
                  'benignBrainTumorPct',
                  getQuoteFieldValue<number>(initialPath.benignBrainTumorPct) * 100,
                  recurrencePath.benignBrainTumorPct
                )
              )}
              helperText={ConditionPercentageError(
                'benignBrainTumorPct',
                getQuoteFieldValue<number>(initialPath.benignBrainTumorPct) * 100,
                recurrencePath.benignBrainTumorPct
              )}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'recurrence-supplemental-conditions-coma',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Coma"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(recurrencePath.comaPct)}
              id={recurrencePath.comaPct}
              onChange={e => updateQuoteFieldValue(recurrencePath.comaPct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError(
                  'comaPct',
                  getQuoteFieldValue<number>(initialPath.comaPct) * 100,
                  recurrencePath.comaPct
                )
              )}
              helperText={ConditionPercentageError(
                'comaPct',
                getQuoteFieldValue<number>(initialPath.comaPct) * 100,
                recurrencePath.comaPct
              )}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'recurrence-supplemental-conditions-loss-of-hearing',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Loss of Hearing"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(recurrencePath.lossOfHearingPct)}
              id={recurrencePath.lossOfHearingPct}
              onChange={e => updateQuoteFieldValue(recurrencePath.lossOfHearingPct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError(
                  'lossOfHearingPct',
                  getQuoteFieldValue<number>(initialPath.lossOfHearingPct) * 100,
                  recurrencePath.lossOfHearingPct
                )
              )}
              helperText={ConditionPercentageError(
                'lossOfHearingPct',
                getQuoteFieldValue<number>(initialPath.lossOfHearingPct) * 100,
                recurrencePath.lossOfHearingPct
              )}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'recurrence-supplemental-conditions-loss-of-sight',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Loss of Sight"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(recurrencePath.lossOfSightPct)}
              id={recurrencePath.lossOfSightPct}
              onChange={e => updateQuoteFieldValue(recurrencePath.lossOfSightPct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError(
                  'lossOfSightPct',
                  getQuoteFieldValue<number>(initialPath.lossOfSightPct) * 100,
                  recurrencePath.lossOfSightPct
                )
              )}
              helperText={ConditionPercentageError(
                'lossOfSightPct',
                getQuoteFieldValue<number>(initialPath.lossOfSightPct) * 100,
                recurrencePath.lossOfSightPct
              )}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'recurrence-supplemental-conditions-loss-of-speech',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Loss of Speech"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(recurrencePath.lossOfSpeechPct)}
              id={recurrencePath.lossOfSpeechPct}
              onChange={e => updateQuoteFieldValue(recurrencePath.lossOfSpeechPct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError(
                  'lossOfSpeechPct',
                  getQuoteFieldValue<number>(initialPath.lossOfSpeechPct) * 100,
                  recurrencePath.lossOfSpeechPct
                )
              )}
              helperText={ConditionPercentageError(
                'lossOfSpeechPct',
                getQuoteFieldValue<number>(initialPath.lossOfSpeechPct) * 100,
                recurrencePath.lossOfSpeechPct
              )}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'recurrence-supplemental-conditions-paralysis',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Paralysis"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(recurrencePath.paralysisPct)}
              id={recurrencePath.paralysisPct}
              onChange={e => updateQuoteFieldValue(recurrencePath.paralysisPct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError(
                  'paralysisPct',
                  getQuoteFieldValue<number>(initialPath.paralysisPct) * 100,
                  recurrencePath.paralysisPct
                )
              )}
              helperText={ConditionPercentageError(
                'paralysisPct',
                getQuoteFieldValue<number>(initialPath.paralysisPct) * 100,
                recurrencePath.paralysisPct
              )}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
        {
          key: 'recurrence-supplemental-conditions-hiv',
          element: (
            <QuoteFormElement
              elementType="Text"
              disabled
              label="Occupational HIV"
              adornment="percentage"
              type="number"
              value={getQuoteFieldValue<number>(recurrencePath.occupationalHIVPct)}
              id={recurrencePath.occupationalHIVPct}
              onChange={e => updateQuoteFieldValue(recurrencePath.occupationalHIVPct, Number(e.target.value) / 100)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              error={isDefined(
                ConditionPercentageError(
                  'occupationalHIVPct',
                  getQuoteFieldValue<number>(initialPath.occupationalHIVPct) * 100,
                  recurrencePath.occupationalHIVPct
                )
              )}
              helperText={ConditionPercentageError(
                'occupationalHIVPct',
                getQuoteFieldValue<number>(initialPath.occupationalHIVPct) * 100,
                recurrencePath.occupationalHIVPct
              )}
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
  ];
  return <FormRowGrid rows={recurrenceDiagnosis} />;
};

export const CriticalIllnessConditionsBlock = () => {
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'critical-illness-conditions-row-1'},
      items: [
        {
          key: 'critical-illness-conditions-column-1',
          element: (
            <Grid container alignItems="center">
              <Grid spacing={1} style={{padding: '0 2rem'}}>
                <h4>Initial Diagnosis (% of Plan Benefit)</h4>
              </Grid>
              <Grid spacing={1} style={{padding: '0 1rem'}}>
                <InitialDiagnosisBlock />
              </Grid>
            </Grid>
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
        {
          key: 'critical-illness-conditions-column-2',
          element: (
            <Grid container alignItems="center">
              <Grid spacing={1} style={{padding: '0 2rem'}}>
                <h4>Recurrence (% of Plan Benefit)</h4>
              </Grid>
              <Grid spacing={1} style={{padding: '0 1rem'}}>
                <RecurrenceDiagnosisBlock />
              </Grid>
            </Grid>
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
      ],
    },
  ];

  return (
    <FormWrapper title="Critical Illness Conditions" collapsible withSeparator>
      <Grid container alignItems="center">
        <FormRowGrid rows={rows} />
      </Grid>
    </FormWrapper>
  );
};
